/**
 * What counts as a link in a message.
 *
 * Both tiers have to agree on this. The function reads the first link in a
 * message to build the card under it; the screen underlines the same text so
 * it can be tapped. If the two disagreed, a message would show a card for one
 * address and underline another.
 */

/**
 * Deliberately loose about what follows the scheme and strict about where a
 * link starts. Trailing punctuation is taken off afterwards rather than
 * excluded here, because whether a full stop belongs to the URL or to the
 * sentence cannot be decided by the character alone.
 */
const LINK = /\bhttps?:\/\/[^\s<>"']+/gi;

/**
 * The link without the sentence it was written in.
 *
 * People write "look at https://example.com/x." and mean the stop to end the
 * sentence. A closing bracket is only part of the link if an opening one was
 * too, so "(see https://example.com/x)" keeps its shape and a link to an
 * article whose own path contains brackets keeps its.
 */
export function trimLink(url: string): string {
  let out = url;

  while (out.length > 0) {
    // slice rather than an index, so this reads as a string under the
    // worker's stricter settings rather than a string that might be missing.
    const last = out.slice(-1);

    if ('.,;:!?'.includes(last)) {
      out = out.slice(0, -1);
      continue;
    }

    if (last === ')' || last === ']') {
      const open = last === ')' ? '(' : '[';
      const opens = out.split(open).length - 1;
      const closes = out.split(last).length - 1;
      if (closes > opens) {
        out = out.slice(0, -1);
        continue;
      }
    }

    break;
  }

  return out;
}

/** Long enough to be an address rather than a scheme and a stray character. */
function looksReal(url: string): boolean {
  const after = url.replace(/^https?:\/\//i, '');
  return after.includes('.') && after.length > 3;
}

/** The first link in a message, or null. */
export function firstLink(text: string | undefined): string | null {
  for (const match of (text ?? '').matchAll(new RegExp(LINK))) {
    const url = trimLink(match[0]);
    if (looksReal(url)) return url;
  }
  return null;
}

export interface Piece {
  text: string;
  /** Set when this piece is a link, and is what it points at. */
  link?: string;
}

/**
 * The message broken into plain runs and links, in order.
 *
 * Returned as data rather than rendered here so this file stays free of React
 * and the Cloud Functions can import it too.
 */
export function splitLinks(text: string): Piece[] {
  const pieces: Piece[] = [];
  let at = 0;

  for (const match of text.matchAll(new RegExp(LINK))) {
    const start = match.index ?? 0;
    const url = trimLink(match[0]);
    if (!looksReal(url)) continue;

    if (start > at) pieces.push({ text: text.slice(at, start) });
    pieces.push({ text: url, link: url });
    at = start + url.length;
  }

  if (at < text.length) pieces.push({ text: text.slice(at) });
  return pieces;
}
